import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormHelperText,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Fade
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Visibility, 
  VisibilityOff,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { builderInvitationService } from '../services/builderInvitationService';

// --- CAROUSEL DATA (Tailored for Sign Up) ---
const carouselSteps = [
  {
    title: "Start Your Project Right",
    description: "Join thousands of UK homeowners using AI to plan renovations, extensions, and conversions."
  },
  {
    title: "Get Accurate Quotes",
    description: "Stop guessing. Create a professional scope of work that gets you comparable, fixed-price quotes."
  },
  {
    title: "For Builders Too",
    description: "Are you a pro? Join our network to receive detailed, ready-to-quote project leads."
  }
];

// --- VALIDATION SCHEMAS ---
const baseSchema = yup.object({
  firstName: yup.string().required('First name is required').min(2, 'Min 2 chars'),
  lastName: yup.string().required('Last name is required').min(2, 'Min 2 chars'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(8, 'Min 8 chars').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain Upper, Lower, and Number').required('Required'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match').required('Confirm password'),
  userType: yup
    .string()
    .oneOf(['homeowner', 'builder'], 'Please select an account type') // Custom error message
    .required('Please select an account type'),
  phone: yup.string().matches(/^(\+44|0)[0-9]{10,11}$/, 'Invalid UK number').optional(),
  agreeToTerms: yup.boolean().oneOf([true], 'Required'),
  agreeToPrivacy: yup.boolean().oneOf([true], 'Required'),
});

const builderSchema = baseSchema.shape({
  invitationCode: yup.string().when('userType', {
    is: 'builder',
    then: (schema) => schema.required('Invitation code required'),
    otherwise: (schema) => schema.optional(),
  }),
  companyName: yup.string().optional(),
});

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { register: registerUser, isAuthenticated, loading } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Builder Logic State
  const [invitationValidated, setInvitationValidated] = useState(false);
  const [projectInfo, setProjectInfo] = useState<{ projectId: string; projectTitle: string } | null>(null);
  const [validatingInvitation, setValidatingInvitation] = useState(false);

  // Carousel State
  const [activeStep, setActiveStep] = useState(0);

  const urlInvitationCode = searchParams.get('code');

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % carouselSteps.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(builderSchema),
    defaultValues: {
      userType: undefined,
      agreeToTerms: false,
      agreeToPrivacy: false,
      invitationCode: urlInvitationCode || '',
    },
  });

  const userType = watch('userType');
  const invitationCode = watch('invitationCode');

  // Builder Validation Logic
  useEffect(() => {
    if (userType === 'builder' && invitationCode && invitationCode.length === 8) {
      validateInvitationCode(invitationCode);
    } else {
      setInvitationValidated(false);
      setProjectInfo(null);
    }
  }, [invitationCode, userType]);

  useEffect(() => {
    if (userType === 'builder' && urlInvitationCode && !invitationCode) {
      setValue('invitationCode', urlInvitationCode);
    }
  }, [userType, urlInvitationCode, invitationCode, setValue]);

  const validateInvitationCode = async (code: string) => {
    if (!code || code.length !== 8) return;
    setValidatingInvitation(true);
    setError('');
    try {
      const result = await builderInvitationService.validateInvitation(code);
      if (result.valid) {
        setInvitationValidated(true);
        setProjectInfo({ projectId: result.projectId!, projectTitle: result.projectTitle! });
      } else {
        setInvitationValidated(false);
        setProjectInfo(null);
        setError('Invalid or expired invitation code');
      }
    } catch (err: any) {
      setInvitationValidated(false);
      setProjectInfo(null);
      setError(err.response?.data?.error || 'Failed to validate code');
    } finally {
      setValidatingInvitation(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (data.userType === 'builder' && !invitationValidated) {
      setError('Please enter a valid invitation code');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await registerUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        userType: data.userType,
        phone: data.phone,
        companyName: data.companyName,
      });

      if (result.needsConfirmation) {
        navigate('/confirm-registration', { 
          state: { 
            email: data.email,
            userType: data.userType,
            invitationCode: data.userType === 'builder' ? data.invitationCode : undefined,
          } 
        });
      } else {
        if (data.userType === 'builder' && data.invitationCode) {
          try {
            await builderInvitationService.acceptInvitation(data.invitationCode);
            navigate('/app/builder/dashboard');
          } catch (err) {
            navigate('/app/dashboard');
          }
        } else {
          navigate('/app/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      
      {/* --- LEFT SIDE: SCROLLABLE FORM --- */}
      <Grid
        item
        xs={12}
        sm={8}
        md={5}
        component={Paper}
        elevation={6}
        square
        sx={{
          height: '100%',
          overflowY: 'auto', // CRITICAL: Allows form to scroll
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: { xs: 3, md: 4 }, // Less padding on mobile
          backgroundColor: '#fff'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 450, my: 'auto' }}>
          
          {/* Back Button */}
          <Box sx={{ mb: 3 }}>
            <Button 
              component={RouterLink}
              to="/"
              startIcon={<ArrowBackIcon />} 
              sx={{ textTransform: 'none', color: 'text.secondary', pl: 0 }}
            >
              Back to Home
            </Button>
          </Box>

          {/* Branding - Centered */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
            <Box component="img" src="/logo.png" alt="Quorite" sx={{ height: 36, mr: 1.5 }} />
          </Box>

          {/* Title - Centered */}
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
            Create Account
          </Typography>

          {/* Subtitle - Centered & Dynamic */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            {!userType 
              ? 'Select an account type to get started.' // Neutral text
              : userType === 'builder' 
                ? 'Join as a builder to access project leads.'
                : 'Join thousands of UK homeowners planning their projects.'
            }
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            
            {/* User Type Selection */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 3, 
                borderRadius: 2, 
                bgcolor: 'grey.50',
                // Add red border if there is an error
                borderColor: errors.userType ? 'error.main' : 'rgba(0, 0, 0, 0.12)' 
              }}
            >
              <FormControl component="fieldset" error={!!errors.userType} fullWidth>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mr: 2, 
                      fontWeight: 600,
                      color: errors.userType ? 'error.main' : 'text.primary' 
                    }}
                  >
                    I am a:
                  </Typography>
                  
                  <Controller
                    name="userType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel 
                          value="homeowner" 
                          control={<Radio size="small" />} 
                          label={<Typography variant="body2">Homeowner</Typography>} 
                          sx={{ mr: 2 }}
                        />
                        <FormControlLabel 
                          value="builder" 
                          control={<Radio size="small" />} 
                          label={<Typography variant="body2">Builder</Typography>} 
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
                {/* This displays the red error text */}
                {errors.userType && (
                  <FormHelperText sx={{ mt: 1 }}>{errors.userType.message}</FormHelperText>
                )}
              </FormControl>
            </Paper>

            {/* Builder Code */}
            <Collapse in={userType === 'builder'}>
              <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Builder Invitation</Typography>
                <TextField
                  {...register('invitationCode')}
                  fullWidth
                  size="small"
                  placeholder="Enter 8-character code"
                  error={!!errors.invitationCode}
                  helperText={errors.invitationCode?.message}
                  inputProps={{ style: { textTransform: 'uppercase' }, maxLength: 8 }}
                  InputProps={{
                    endAdornment: validatingInvitation ? <CircularProgress size={20} /> : 
                                  invitationValidated ? <Chip label="Valid" color="success" size="small" /> : null
                  }}
                  sx={{ bgcolor: 'white' }}
                />
                {projectInfo && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'success.main', fontWeight: 600 }}>
                    ✓ For Project: {projectInfo.projectTitle}
                  </Typography>
                )}
              </Box>
            </Collapse>

            {/* Name Fields */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  {...register('firstName')}
                  fullWidth
                  label="First Name"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('lastName')}
                  fullWidth
                  label="Last Name"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </Grid>
            </Grid>

            <TextField
              {...register('email')}
              fullWidth
              label="Email Address"
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{ mb: 2 }}
            />

            <Collapse in={userType === 'builder'}>
              <TextField
                {...register('companyName')}
                fullWidth
                label="Company Name"
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                sx={{ mb: 2 }}
              />
            </Collapse>

            <TextField
              {...register('phone')}
              fullWidth
              label="Phone Number (Optional)"
              placeholder="+44..."
              error={!!errors.phone}
              helperText={errors.phone?.message}
              sx={{ mb: 2 }}
            />

            <TextField
              {...register('password')}
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              error={!!errors.password}
              helperText={errors.password?.message}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register('confirmPassword')}
              fullWidth
              label="Confirm Password"
              type="password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              sx={{ mb: 3 }}
            />

            {/* Terms */}
            <Box sx={{ mb: 3 }}>
              <Controller
                name="agreeToTerms"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} size="small" />}
                    label={<Typography variant="body2">I agree to <Link href="/terms">Terms</Link></Typography>}
                  />
                )}
              />
              <FormHelperText error>{errors.agreeToTerms?.message}</FormHelperText>

              <Controller
                name="agreeToPrivacy"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} size="small" />}
                    label={<Typography variant="body2">I agree to <Link href="/privacy">Privacy Policy</Link></Typography>}
                  />
                )}
              />
              <FormHelperText error>{errors.agreeToPrivacy?.message}</FormHelperText>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || (userType === 'builder' && !invitationValidated)}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                mb: 3
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
            </Button>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">OR</Typography>
            </Divider>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  component={RouterLink}
                  to={`/login${urlInvitationCode ? `?code=${urlInvitationCode}` : ''}`}
                  variant="body2"
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
          </Box>
          
          {/* Footer */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Quorite. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* --- RIGHT SIDE: IMAGE & CAROUSEL --- */}
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1581094794329-c8112a89af12?q=80&w=2000&auto=format&fit=crop)', // Different image for Register
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) => t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.85)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Box sx={{ maxWidth: 600, minHeight: 200 }}>
            {carouselSteps.map((step, index) => (
              <Fade in={activeStep === index} timeout={500} key={index}>
                <Box sx={{ display: activeStep === index ? 'block' : 'none' }}>
                  <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9, lineHeight: 1.6 }}>
                    "{step.description}"
                  </Typography>
                </Box>
              </Fade>
            ))}
            
            <Box sx={{ mt: 5, display: 'flex', gap: 1.5, justifyContent: 'center' }}>
               {carouselSteps.map((_, index) => (
                 <Box 
                   key={index}
                   onClick={() => setActiveStep(index)}
                   sx={{ 
                     width: 10, height: 10, borderRadius: '50%', bgcolor: 'white', 
                     opacity: activeStep === index ? 1 : 0.4,
                     cursor: 'pointer',
                     transition: 'all 0.3s ease',
                     '&:hover': { opacity: 0.8, transform: 'scale(1.2)' }
                   }} 
                 />
               ))}
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default RegisterPage;